import { CampaignCardBottom, Discount } from '@teliaee/telia-common-components';
import { TranslationsContext } from '@teliaee/telia-translate';
import { observer } from 'mobx-react';
import React from 'react';
import config from '../Config';
import { CALLS_PACKAGE_ID, CampaignInfo } from '../stores/MobileLifeStore';
import { useStores } from '../stores/RootStore';
import { formatPrice, getPrivateMobileLifeBasePrice, mobileLifePricePerUser, mobileLifeTotalPrice } from '../util/PriceUtil';
import { PrivateMobileCardProps } from './PrivateMobileCard';

type PrivateMobileCardPriceProps = Pick<
  PrivateMobileCardProps,
  | 'packageIds'
  | 'callsPackagePrice'
  | 'forSingleUserOnly'
  | 'campaigns'
  | 'multiUserDistributedPrice'
  | 'singleUserDistributedPrice'
  | 'packageCustomDistributedPrices'
  | 'inModal'
>;

const PrivateMobileCardPrice = observer((props: PrivateMobileCardPriceProps) => {
  const { forSingleUserOnly, campaigns, multiUserDistributedPrice, singleUserDistributedPrice, packageCustomDistributedPrices, inModal } = props;
  const { mobileLifeStore, languageStore } = useStores();
  const { translate } = React.useContext(TranslationsContext);
  const packageId = mobileLifeStore.getPackageId(props.packageIds);
  const internetPrices = mobileLifeStore.getPackagePrices(packageId);
  const hasMultipleCampaigns = campaigns?.length > 1;

  const hasPackageDiscount = (): boolean => {
    const hasInternetDiscount = mobileLifeStore.hasPackageDiscount(packageId);
    const hasCallsDiscount = mobileLifeStore.hasPackageDiscount(CALLS_PACKAGE_ID);
    return hasInternetDiscount || hasCallsDiscount;
  };

  const getCallsPrice = (): number => {
    if (mobileLifeStore.hasPackageDiscount(CALLS_PACKAGE_ID)) {
      return mobileLifeStore.getPackagePrices(CALLS_PACKAGE_ID).price;
    }
    return props.callsPackagePrice;
  };

  const callsPrice = getCallsPrice();
  const hasDiscount = hasPackageDiscount();
  const hasCampaign = !hasDiscount && campaigns?.length > 0;

  const basePrice = getPrivateMobileLifeBasePrice(
    mobileLifeStore.selectedDeviceCount,
    internetPrices?.price,
    multiUserDistributedPrice,
    singleUserDistributedPrice,
    packageCustomDistributedPrices,
    hasDiscount
  );

  const getCampaignPrice = (campaign?: CampaignInfo) =>
    getPrivateMobileLifeBasePrice(
      mobileLifeStore.selectedDeviceCount,
      internetPrices?.price,
      campaign?.multiUserDistributedCampaignPrice ?? multiUserDistributedPrice,
      campaign?.singleUserDistributedCampaignPrice ?? singleUserDistributedPrice,
      campaign?.packageCustomDistributedCampaignPrices?.length === 5 ? campaign.packageCustomDistributedCampaignPrices : packageCustomDistributedPrices,
      hasDiscount
    );

  const getTotalPrice = (campaignPrice?: number) => {
    return mobileLifeTotalPrice(campaignPrice ?? basePrice, callsPrice, forSingleUserOnly ? 1 : mobileLifeStore.selectedDeviceCount);
  };

  const getRegularPrice = (): string => {
    if (!hasDiscount && !hasCampaign) {
      return formatPrice(getTotalPrice());
    }
    return formatPrice(
      mobileLifeTotalPrice(+(internetPrices?.priceWithoutDiscount ?? basePrice), +callsPrice, forSingleUserOnly ? 1 : mobileLifeStore.selectedDeviceCount)
    );
  };

  const getDiscountInfoFromCampaign = (campaign: CampaignInfo): Discount => {
    const campaignTotalPrice = getTotalPrice(getCampaignPrice(campaign));

    return {
      id: campaign?.id,
      price:
        (campaign?.customPricePreText ? `${campaign?.customPricePreText} ` : '') +
        translate(`mobile.package.${hasDiscount ? 'discount' : 'campaign'}.eurPerMonth`, { price: formatPrice(campaignTotalPrice) }),
      pricePerUser:
        mobileLifeStore.selectedDeviceCount > 1 &&
        `${formatPrice(mobileLifePricePerUser(campaignTotalPrice, mobileLifeStore.selectedDeviceCount))} ${translate('mobile.package.user')}`,
      conditionsLink: (!campaign?.rulesLinkOnlyInModal || inModal) && campaign?.rulesLink,
      tooltipText: campaign?.tooltipText,
      preText: campaign?.endDate ? campaign.endDate : undefined
    };
  };

  const discountsForCardBottom = (): Discount[] => {
    if (!hasCampaign && !hasDiscount) {
      return [];
    }

    return [getDiscountInfoFromCampaign(campaigns?.[0]), ...(hasMultipleCampaigns ? [getDiscountInfoFromCampaign(campaigns?.[1])] : [])];
  };

  return (
    <CampaignCardBottom
      regularPrice={`${getRegularPrice()} ${translate('mobile.eurPerMonth')}`}
      regularPricePerUser={
        mobileLifeStore.selectedDeviceCount > 1 &&
        `${formatPrice(mobileLifePricePerUser(getTotalPrice(), mobileLifeStore.selectedDeviceCount))} ${translate('mobile.package.user')}`
      }
      language={languageStore.currentLanguage}
      app="public"
      tier={config.componentTier}
      discounts={discountsForCardBottom()}
      hideSeparatorOnTop={true}
    />
  );
});

export default PrivateMobileCardPrice;
